/**
 * Analyzer: Trade Narrative (per-trade scope)
 *
 * Synthesizes a single trade's bar-context signals into a one-paragraph
 * Coach narrative. Where `tradeFlags` produces atomic chips and the
 * day/setup analyzers produce aggregate observations, this one tells the
 * story of THIS trade specifically — what the trader did right, what they
 * left on the table, and what the lesson is.
 *
 * Different from tradeFlags:
 *   - tradeFlags emits chip-shaped flags (key/label/severity/hint)
 *   - this analyzer emits Observation-shaped narratives with title + detail
 *     + action + structured Coach actions (Tag mistake, Add lesson, etc.)
 *
 * Different from day/setup analyzers:
 *   - those operate on populations of trades
 *   - this one zooms into a single trade and grades it on its own merits
 *
 * Patterns this round (one observation each, prioritized — only the strongest
 * narrative fires per trade so the Coach stays focused):
 *
 *   A-GRADE   Sharp entry + healthy capture + HTF aligned (success)
 *   GIFT      Stop-out on a near-target wick (warning, jump-to-trade context)
 *   LEFT-IT   Winner that capped <30% of the available move (warning)
 *   ROUND     Round-trip — big MFE, kept little (warning)
 *   FOUGHT    Loser whose primary failure was fighting the bigger trend (info)
 *   FULL-1R   Loser that went the full distance to stop (warning, entry-timing focus)
 *   CLEAN-LOSER Lost despite a clean read — variance, not process (info)
 *
 * Reads from ctx:
 *   trade — the full trade object (with barAnalysis already populated)
 */

import { CATEGORY, SEVERITY, makeObservation, makeAction, ACTION_TYPE } from '../../types.js';

export const key = 'tradeNarrative';
export const tier = 'free';

const fmtPct = (n) => `${Math.round(n * 100)}%`;

// Period-list-aware EMA stack classifier (mirrors the others). Returns
// 'with' | 'against' | 'neutral' relative to the trade's direction.
const classifyStack = (emas, direction) => {
  if (!emas || typeof emas !== 'object') return 'neutral';
  const periods = Object.keys(emas)
    .map(k => parseInt(k, 10))
    .filter(n => Number.isFinite(n) && n >= 1)
    .sort((a, b) => a - b);
  if (periods.length < 2) return 'neutral';
  const values = periods.map(p => emas[p] ?? emas[String(p)]);
  if (!values.every(v => Number.isFinite(v))) return 'neutral';
  let bullish = true, bearish = true;
  for (let i = 0; i < values.length - 1; i++) {
    if (values[i] <= values[i + 1]) bullish = false;
    if (values[i] >= values[i + 1]) bearish = false;
  }
  if (!bullish && !bearish) return 'neutral';
  const isLong = direction !== 'short';
  if (bullish) return isLong ? 'with' : 'against';
  return isLong ? 'against' : 'with';
};

// Compute capture ratio = realized / available favorable. Returns null when
// the inputs aren't usable.
const captureRatio = (trade, ba) => {
  const netPnl = Number(trade?.netPnl);
  const mfe = Number(ba?.mfeDollars);
  if (!Number.isFinite(netPnl) || !Number.isFinite(mfe) || mfe <= 0) return null;
  return Math.max(0, Math.min(1, netPnl / mfe));
};

// Build the standard "Add to Lessons" action when the trade belongs to a
// session group. No session → no action.
const lessonAction = (trade, lessonText) => {
  if (!trade?.sessionId || !lessonText) return null;
  return makeAction({
    type: ACTION_TYPE.ADD_LESSON_TO_SESSION,
    label: 'Save to Lessons',
    payload: { sessionId: trade.sessionId, lesson: lessonText },
    doneLabel: 'Saved ✓',
  });
};

const tagMistakeAction = (trade, mistakeName) => {
  if (!trade?.id || !mistakeName) return null;
  return makeAction({
    type: ACTION_TYPE.TAG_TRADE_MISTAKE,
    label: `Tag as "${mistakeName}"`,
    payload: { tradeIds: [trade.id], mistake: mistakeName },
    confirmText: `Tag this trade as "${mistakeName}"?`,
    doneLabel: 'Tagged ✓',
  });
};

// ── Public analyze() ──────────────────────────────────────────────────────

export const analyze = (ctx) => {
  const trade = ctx?.trade;
  if (!trade || typeof trade !== 'object') return null;
  const ba = trade.barAnalysis;
  if (!ba || typeof ba !== 'object') return null;

  const netPnl = Number.isFinite(trade.netPnl) ? trade.netPnl : null;
  if (netPnl == null) return null;
  const isWinner = netPnl > 0;
  const isLoser = netPnl < 0;
  const { maeR, mfeR } = ba;
  if (!Number.isFinite(maeR) || !Number.isFinite(mfeR)) return null;

  const capture = captureRatio(trade, ba);
  const entryStance = classifyStack(ba.emasAtEntry, trade.direction);

  // HTF stance summary — picks the "biggest" disagreeing TF if any.
  const htfStances = {};
  if (ba.htfEmas && typeof ba.htfEmas === 'object') {
    for (const [tf, emas] of Object.entries(ba.htfEmas)) {
      const s = classifyStack(emas, trade.direction);
      if (s !== 'neutral') htfStances[tf] = s;
    }
  }
  const htfTfs = Object.keys(htfStances);
  const htfWith = htfTfs.filter(tf => htfStances[tf] === 'with');
  const htfAgainst = htfTfs.filter(tf => htfStances[tf] === 'against');
  // Prefer 4h > 1h > 15m for "against" labelling (heaviest-weight first).
  const tfWeight = { '4h': 4, '2h': 3, '1h': 2, '30m': 1, '15m': 0 };
  const dominantAgainst = htfAgainst.sort((a, b) => (tfWeight[b] ?? -1) - (tfWeight[a] ?? -1))[0] || null;
  const fullyAligned = htfTfs.length > 0 && htfAgainst.length === 0 && htfWith.length === htfTfs.length;

  // ── Pattern A-GRADE ────────────────────────────────────────────────────
  if (isWinner && maeR <= 0.20 && mfeR >= 0.5 && capture != null && capture >= 0.65 && fullyAligned) {
    const lesson = `A-grade trade — sharp entry (${fmtPct(maeR)} MAE), ${fmtPct(capture)} capture, fully HTF-aligned. Repeatable when the read is this clean.`;
    return [makeObservation({
      id: `${key}:${trade.id}:a-grade`,
      analyzer: key,
      scope: 'trade',
      scopeId: String(trade.id),
      severity: SEVERITY.SUCCESS,
      category: CATEGORY.PROCESS,
      title: 'A-grade trade — clean entry, real capture, HTF-aligned',
      detail: `Sharp entry (${fmtPct(maeR)} of risk against you), ${fmtPct(capture)} of available move captured, all HTFs (${htfTfs.join(', ')}) aligned with direction.`,
      action: 'Note the conditions in your Lessons box — A-grade trades are repeatable when you can name what made them.',
      actions: [lessonAction(trade, lesson)].filter(Boolean),
      evidence: { maeR, mfeR, capture, htfStances },
      confidence: 0.86,
      priority: 80,
    })];
  }

  // ── Pattern GIFT (stop-out on a near-target wick) ─────────────────────
  if (isLoser && mfeR >= 0.80 && maeR >= 0.95) {
    return [makeObservation({
      id: `${key}:${trade.id}:gift`,
      analyzer: key,
      scope: 'trade',
      scopeId: String(trade.id),
      severity: SEVERITY.WARNING,
      category: CATEGORY.EXECUTION,
      title: `Wicked out at ${fmtPct(mfeR)} of target — stop placement gave nothing back`,
      detail: `MFE reached ${fmtPct(mfeR)} of risk before reversing into the stop. The market was reaching toward your level — your stop was inside the volatility.`,
      action: `Loosen stop by 0.2-0.3R or move it to a structural level (prior swing, EMA reclaim) for this setup.`,
      actions: [tagMistakeAction(trade, 'Stop Too Tight')].filter(Boolean),
      evidence: { maeR, mfeR },
      confidence: 0.82,
      priority: 78,
    })];
  }

  // ── Pattern ROUND (round-trip — big MFE, modest result) ──────────────
  if (mfeR >= 1.5 && capture != null && capture > 0 && capture <= 0.40) {
    return [makeObservation({
      id: `${key}:${trade.id}:round-trip`,
      analyzer: key,
      scope: 'trade',
      scopeId: String(trade.id),
      severity: SEVERITY.WARNING,
      category: CATEGORY.EXECUTION,
      title: `Round-tripped — reached ${mfeR.toFixed(1)}R favorable, kept ${fmtPct(capture)}`,
      detail: `Trade went ${mfeR.toFixed(1)}R in your favor before unwinding. ${fmtPct(capture)} capture means you watched most of the runner die.`,
      action: 'Set a structural trail after the first runner target hits — prior swing, last reclaim, or BE+1R floor.',
      actions: [tagMistakeAction(trade, 'Held Through Reversal')].filter(Boolean),
      evidence: { mfeR, capture },
      confidence: 0.8,
      priority: 76,
    })];
  }

  // ── Pattern LEFT-IT (winner that capped low) ─────────────────────────
  if (isWinner && capture != null && capture < 0.30) {
    return [makeObservation({
      id: `${key}:${trade.id}:left-it`,
      analyzer: key,
      scope: 'trade',
      scopeId: String(trade.id),
      severity: SEVERITY.WARNING,
      category: CATEGORY.EXECUTION,
      title: `Winner capped at ${fmtPct(capture)} of available move`,
      detail: `Net P&L was ${fmtPct(capture)} of the favorable excursion. Targets were inside the profit zone, not at the edge.`,
      action: 'Push the next target out, or convert one target into a runner with a trailing stop.',
      actions: [tagMistakeAction(trade, 'Took Profit Too Early')].filter(Boolean),
      evidence: { capture, mfeR },
      confidence: 0.78,
      priority: 72,
    })];
  }

  // ── Pattern FULL-1R (full risk loss, no MFE) ─────────────────────────
  if (isLoser && maeR >= 0.95 && mfeR < 0.30) {
    return [makeObservation({
      id: `${key}:${trade.id}:full-1r`,
      analyzer: key,
      scope: 'trade',
      scopeId: String(trade.id),
      severity: SEVERITY.WARNING,
      category: CATEGORY.EXECUTION,
      title: `Full 1R loss with no favorable excursion`,
      detail: `MAE pushed all the way to stop (${fmtPct(maeR)}), MFE only ${fmtPct(mfeR)}. The trade never worked even briefly.`,
      action: `Was this a missed entry trigger or wrong-side-of-trend? Pull up the chart and decide which one to fix.`,
      actions: [tagMistakeAction(trade, 'Bad Entry Timing')].filter(Boolean),
      evidence: { maeR, mfeR },
      confidence: 0.78,
      priority: 70,
    })];
  }

  // ── Pattern FOUGHT (loser primarily attributable to fighting HTF) ───
  if (isLoser && dominantAgainst && entryStance === 'with') {
    const tfLabel = dominantAgainst.toUpperCase();
    return [makeObservation({
      id: `${key}:${trade.id}:fought-htf:${dominantAgainst}`,
      analyzer: key,
      scope: 'trade',
      scopeId: String(trade.id),
      severity: SEVERITY.INFO,
      category: CATEGORY.SETUP,
      title: `Took an entry-TF read that was fighting the ${tfLabel}`,
      detail: `Entry-TF stack agreed with your direction, but the ${tfLabel} stack was opposed. Higher-frame structure won this one.`,
      action: `Add "Is the ${tfLabel} stack with me?" to this setup's checklist — it's the gate that would have caught this trade.`,
      evidence: { entryStance, htfStances, dominantAgainst },
      confidence: 0.7,
      priority: 64,
    })];
  }

  // ── Pattern CLEAN-LOSER (sharp entry, modest MFE, lost — variance) ──
  if (isLoser && maeR <= 0.30 && mfeR >= 0.5 && mfeR < 0.85) {
    return [makeObservation({
      id: `${key}:${trade.id}:clean-loser`,
      analyzer: key,
      scope: 'trade',
      scopeId: String(trade.id),
      severity: SEVERITY.INFO,
      category: CATEGORY.PROCESS,
      title: `Lost despite a clean read — variance, not process`,
      detail: `Sharp entry (${fmtPct(maeR)} adverse), reached ${fmtPct(mfeR)} favorable, then turned. The trade was right; the outcome wasn't.`,
      action: `Don't over-correct. This is variance — your process did the right thing. Note it, move on.`,
      evidence: { maeR, mfeR },
      confidence: 0.65,
      priority: 50,
    })];
  }

  // No dominant pattern fired — return null. The flag chips already cover
  // smaller/atomic signals on this trade; we don't need to narrate every one.
  return null;
};

// Exports for tests
export { classifyStack, captureRatio };
